import React, { useState } from 'react';
import { toast } from 'sonner';

const RecurringMissionModal = ({ isOpen, onClose, clients, housings, employees, onSubmit }) => {
  const [formData, setFormData] = useState({
    client_id: '',
    housing_id: '',
    employee_id: '',
    is_divers: false,
    lieu_custom: '',
    date_debut: '',
    date_fin: '',
    notes: '',
    prix: '',
    prix_intervenant: '',
    // Jours de la semaine
    lundi: false,
    mardi: false,
    mercredi: false,
    jeudi: false,
    vendredi: false,
    samedi: false,
    dimanche: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.date_debut || !formData.date_fin) {
      toast.warning("Veuillez sélectionner une date de début et de fin");
      return;
    }
    
    const joursSemaine = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
    const joursSelectionnes = joursSemaine.filter(jour => formData[jour]);
    
    if (joursSelectionnes.length === 0) {
      toast.warning("Veuillez sélectionner au moins un jour de la semaine");
      return;
    }
    
    if (!formData.is_divers && !formData.housing_id) {
      toast.warning('Veuillez sélectionner un logement ou cocher "Mission DIVERS"');
      return;
    }
    
    onSubmit({
      ...formData,
      joursSelectionnes
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">🔄 Créer des Missions Récurrentes</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-3xl"
            >
              ✕
            </button>
          </div>
          <p className="text-sm mt-2 text-gray-100">
            Générer automatiquement des missions répétitives selon un planning
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Type de mission */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                name="is_divers"
                checked={formData.is_divers}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span className="font-medium text-gray-700">Mission DIVERS (sans logement associé)</span>
            </label>
          </div>

          {/* Client et Logement */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client
              </label>
              <select
                name="client_id"
                value={formData.client_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sélectionner un client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.prenom} {client.nom}
                  </option>
                ))}
              </select>
            </div>

            {!formData.is_divers ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logement *
                </label>
                <select
                  name="housing_id"
                  value={formData.housing_id}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Sélectionner un logement</option>
                  {housings.map(housing => (
                    <option key={housing.id} value={housing.id}>
                      {housing.pseudo || housing.adresse}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lieu personnalisé
                </label>
                <input
                  type="text"
                  name="lieu_custom"
                  value={formData.lieu_custom}
                  onChange={handleChange}
                  placeholder="Ex: Bureau, Garage..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>

          {/* Employé */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employé assigné
            </label>
            <select
              name="employee_id"
              value={formData.employee_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Non assigné</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.prenom} {employee.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Période */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de début *
              </label>
              <input
                type="date"
                name="date_debut"
                value={formData.date_debut}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin *
              </label>
              <input
                type="date"
                name="date_fin"
                value={formData.date_fin}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Jours de la semaine */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Jours de répétition *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'lundi', label: 'Lundi', emoji: '📅' },
                { name: 'mardi', label: 'Mardi', emoji: '📅' },
                { name: 'mercredi', label: 'Mercredi', emoji: '📅' },
                { name: 'jeudi', label: 'Jeudi', emoji: '📅' },
                { name: 'vendredi', label: 'Vendredi', emoji: '📅' },
                { name: 'samedi', label: 'Samedi', emoji: '📅' },
                { name: 'dimanche', label: 'Dimanche', emoji: '📅' },
              ].map(jour => (
                <label key={jour.name} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name={jour.name}
                    checked={formData[jour.name]}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{jour.emoji} {jour.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Prix */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix facturé au client (€)
              </label>
              <input
                type="number"
                step="0.01"
                name="prix"
                value={formData.prix}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix payé à l'employé (€)
              </label>
              <input
                type="number"
                step="0.01"
                name="prix_intervenant"
                value={formData.prix_intervenant}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Instructions ou remarques..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-medium"
            >
              🔄 Générer les missions
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecurringMissionModal;
